import { ArrowLeft, ArrowRight, Leaf, Sparkles, ShoppingBag, ChevronLeft, ChevronRight, Gift, Users, BadgeCheck, Send, MessageCircle, Bookmark, MoreHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useLanguage } from "../i18n/LanguageContext";
import { FallingLeaves } from "../components/FallingLeaves";
import { SEO } from "../components/SEO";
import { FeaturedArticleModal } from "../components/FeaturedArticleModal";
import { SoapComingSoon } from "../components/SoapComingSoon";
import { useState, useRef, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const FEATURED_ARTICLE_SLUG = "sweat-no-smell";

// Hero product image - amber jar with natural cream texture
const HERO_PRODUCT_IMAGE = "/images/hero-product.jpg";

// Path images - updated community image
const PATH_IMAGES = {
  diy: "/images/path-diy.jpg",
  product: "/images/path-product.jpg",
  community: "/images/path-community.jpg",
  diyKit: "/images/path-diykit.jpg"
};

// Mobile Carousel Component with Tabs and Auto-rotate
const MobilePathsCarousel = ({ paths, language }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Auto-rotate every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % paths.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [paths.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < paths.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else if (diff < 0 && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
      }
    }
  };

  const goToSlide = (index) => setCurrentIndex(index);
  
  const handleTabClick = (index, href) => {
    if (index === currentIndex) {
      // Already selected - navigate to the page
      window.location.href = href;
    } else {
      // Not selected - just switch to this tab
      setCurrentIndex(index);
    }
  };

  return (
    <div className="relative">
      {/* Tab Navigation - Premium Pill Buttons */}
      <div className="flex justify-center gap-2 mb-6 px-4">
        {paths.map((path, index) => (
          <button
            key={index}
            onClick={() => handleTabClick(index, path.href)}
            className={`relative px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-[#4A5D43] text-white shadow-lg shadow-[#4A5D43]/30' 
                : 'bg-[#F5F2EB] text-[#6B7266] hover:bg-[#E8E4DC] hover:text-[#4A5D43]'
            }`}
          >
            <span className="flex items-center gap-2">
              {path.badge && index === currentIndex && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-white/20 text-white">
                  {path.badge}
                </span>
              )}
              <span>{path.title}</span>
            </span>
            {/* Active indicator dot */}
            {index === currentIndex && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#4A5D43]" />
            )}
          </button>
        ))}
      </div>

      {/* Carousel */}
      <div 
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(${language === 'he' ? currentIndex * 100 : -currentIndex * 100}%)` }}
        >
          {paths.map((path, index) => (
            <div key={index} className="min-w-full px-4">
              <Link to={path.href} className="block group">
                <div className="rounded-2xl overflow-hidden bg-white shadow-lg shadow-black/5 border border-[#E8E4DC]">
                  {/* Image Container */}
                  <div className="relative h-52 overflow-hidden">
                    <img 
                      src={path.image} 
                      alt={path.title}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Gradient Overlay - stronger for readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C3329]/90 via-[#2C3329]/40 to-transparent" />
                    
                    {/* Text on Image - White with shadow for max readability */}
                    <div className="absolute bottom-0 right-0 left-0 p-5">
                      <div className="flex items-center gap-2 mb-2">
                        {path.badge && (
                          <span className="text-[10px] px-2.5 py-1 rounded-full font-bold bg-[#4A5D43] text-white shadow-md">
                            {path.badge}
                          </span>
                        )}
                      </div>
                      <h3 className="font-serif text-2xl font-semibold text-white drop-shadow-lg mb-1">
                        {path.title}
                      </h3>
                      <span className="text-sm text-white/90 drop-shadow-md font-medium">
                        {path.subtitle}
                      </span>
                    </div>
                  </div>
                  
                  {/* Description Card - Improved Design */}
                  <div className="p-5 bg-gradient-to-b from-[#FAFAF8] to-white">
                    <p className="text-sm text-[#4A5D43] leading-relaxed text-center font-medium">
                      {path.description}
                    </p>
                    {/* CTA Arrow */}
                    <div className="flex justify-center mt-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A5D43]/70 group-hover:text-[#4A5D43] transition-colors">
                        {language === 'he' ? 'לחצו להמשך' : 'Click to continue'}
                        <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
      
      {/* Swipe indicator */}
      <p className="text-center text-xs text-[#9A9790] mt-3">
        {language === 'he' ? '← החליקו לעבור בין האפשרויות →' : '← Swipe to see more →'}
      </p>
    </div>
  );
};

export const Home = () => {
  const { t, language } = useLanguage();
  const Arrow = language === 'he' ? ArrowLeft : ArrowRight;
  const [showArticle, setShowArticle] = useState(false);

  const openArticle = () => {
    setShowArticle(true);
    // Fire-and-forget: count every expand click (general, no auth)
    fetch(`${BACKEND_URL}/api/articles/${FEATURED_ARTICLE_SLUG}/view`, { method: "POST" }).catch(() => {});
  };

  const paths = [
    {
      href: "/diy",
      image: PATH_IMAGES.diy,
      title: t.home.paths.diy.title,
      subtitle: t.home.paths.diy.subtitle,
      description: t.home.paths.diy.description,
      icon: Leaf,
      color: "#4A5D43",
      badge: language === 'he' ? 'בחינם' : 'Free'
    },
    {
      href: "/diy-kit",
      image: PATH_IMAGES.diyKit,
      title: language === 'he' ? 'ערכה להכנה' : 'DIY Kit',
      subtitle: '90 ₪',
      description: language === 'he' ? 'כל החומרים והכלים להכנת דאודורנט בבית' : 'All materials and tools to make deodorant at home',
      icon: Gift,
      color: "#D97706",
      badge: language === 'he' ? 'חדש!' : 'New!'
    },
    {
      href: "/product",
      image: PATH_IMAGES.product,
      title: t.home.paths.buy.title,
      subtitle: t.home.paths.buy.subtitle,
      description: t.home.paths.buy.description,
      icon: ShoppingBag,
      color: "#C47F64"
    },
    {
      href: "/community",
      image: PATH_IMAGES.community,
      title: t.home.paths.community.title,
      subtitle: t.home.paths.community.subtitle,
      description: language === 'he' ? 'יצירות של הקהילה שלנו' : 'Creations from our community',
      icon: Users,
      color: "#2C3329"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO page="home" />
      {/* Hero Section with falling leaves */}
      <section 
        data-testid="hero-section" 
        className="relative pt-20 pb-2 sm:pt-24 sm:pb-4 px-4 sm:px-6"
      >
        {/* Falling Leaves Animation - starts from above and falls elegantly */}
        <div className="absolute inset-0 overflow-visible pointer-events-none" style={{ top: '-100px', height: 'calc(100% + 200px)' }}>
          <FallingLeaves />
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5] via-[#F8F6F2] to-transparent pointer-events-none" />
        <div className="absolute top-16 right-10 w-60 h-60 bg-[#4A5D43]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-[#C47F64]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative max-w-4xl mx-auto text-center z-20">
          <h1 
            data-testid="hero-title"
            className="text-[1.85rem] leading-[1.3] sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-[#2C3329] mb-4 animate-fade-in-up"
          >
            {language === 'he' ? (
              <>
                <span className="block sm:inline">דאודורנט טבעי.</span>
                <span className="block sm:inline"> הידע חופשי.</span>
              </>
            ) : (
              <>
                <span className="block sm:inline">Natural Deodorant.</span>
                <span className="block sm:inline"> Knowledge First.</span>
              </>
            )}
          </h1>
          
          <p 
            data-testid="hero-subtitle"
            className="text-[0.95rem] leading-[1.6] sm:text-base text-[#6B7266] max-w-md mx-auto mb-4 animate-fade-in-up animation-delay-200"
          >
            {language === 'he' ? (
              <>
                <span className="block">נוסחה פתוחה לייצור דאודורנט טבעי</span>
                <span className="block">שפותחה בעזרת בינה מלאכותית.</span>
                <span className="block mt-1">חינם להכנה עצמית או לרכוש מוכן.</span>
                <span className="block mt-1 text-[#4A5D43] font-semibold">מרכיבים טבעיים ושקיפות מלאה.</span>
              </>
            ) : (
              <>
                <span className="block">Open formula for natural deodorant</span>
                <span className="block">developed with AI assistance.</span>
                <span className="block mt-1">Free DIY or purchase ready-made.</span>
                <span className="block mt-1 text-[#4A5D43] font-semibold">Natural ingredients, full transparency.</span>
              </>
            )}
          </p>
        </div>
      </section>

      {/* Hero Product Banner - Full width with gentle animation */}
      <section className="relative w-full overflow-hidden">
        <div 
          className="relative w-full h-56 sm:h-72 md:h-80 cursor-pointer hero-banner-container"
          onClick={(e) => {
            const container = e.currentTarget;
            container.classList.remove('banner-clicked');
            // Force reflow
            void container.offsetWidth;
            container.classList.add('banner-clicked');
          }}
        >
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#C47F64]/10 via-transparent to-[#4A5D43]/10" />
          
          {/* Product image - centered on cream and jar edge */}
          <img 
            src={HERO_PRODUCT_IMAGE}
            alt={language === 'he' ? "דאודורנט טבעי M'ART - מיכל אמבר עם קרם לבן" : "M'ART Natural Deodorant - Amber jar with white cream"}
            className="w-full h-full object-cover hero-banner-image"
            style={{ objectPosition: 'center 35%' }}
          />
          
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5]/20 via-transparent to-[#F5F2EB]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5]/10 via-transparent to-[#FAF8F5]/10" />
        </div>
      </section>

      {/* Paths Section */}
      <section 
        data-testid="paths-section" 
        className="relative py-8 sm:py-10 px-0 sm:px-6 section-warm"
      >
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-6 sm:mb-8 px-4">
              {/* Section Label */}
              <span className="inline-block text-xs font-semibold tracking-widest text-[#4A5D43]/60 uppercase mb-2">
                {language === 'he' ? 'בחרו את הדרך שלכם' : 'Choose Your Path'}
              </span>
              {/* Main Title */}
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#2C3329] font-medium">
                {t.home.paths.title}
              </h2>
              {/* Decorative line */}
              <div className="flex items-center justify-center gap-2 mt-3">
                <span className="w-8 h-px bg-[#4A5D43]/20" />
                <span className="text-[#4A5D43]/40">🌿</span>
                <span className="w-8 h-px bg-[#4A5D43]/20" />
              </div>
            </div>
            
            {/* Mobile Carousel */}
            <div className="md:hidden">
              <MobilePathsCarousel paths={paths} language={language} />
            </div>

            {/* Desktop Grid - 4 columns for 4 paths */}
            <div className="hidden md:grid md:grid-cols-4 gap-5 px-4">
              {paths.map((path, index) => (
                <Link 
                  key={index}
                  to={path.href} 
                  className="group premium-card rounded-2xl overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-[#E8E4DC]/50"
                >
                  <div className="relative h-40">
                    <img 
                      src={path.image} 
                      alt={path.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    {path.badge && (
                      <span className="absolute top-3 right-3 text-[10px] bg-[#4A5D43] text-white px-2.5 py-1 rounded-full font-bold shadow-lg">
                        {path.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col items-center text-center flex-1 bg-gradient-to-b from-white to-[#FAF8F5]">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 -mt-9 relative z-10 shadow-xl transition-transform group-hover:scale-110 bg-white border-2"
                      style={{ borderColor: `${path.color}40` }}
                    >
                      <path.icon className="w-5 h-5" style={{ color: path.color }} />
                    </div>
                    <h3 className="font-serif text-lg text-[#2C3329] mb-1 group-hover:text-[#4A5D43] transition-colors">{path.title}</h3>
                    <span className="text-xs font-bold mb-2 px-3 py-1 rounded-full" style={{ color: path.color, backgroundColor: `${path.color}12` }}>{path.subtitle}</span>
                    <p className="text-xs text-[#6B7266] leading-relaxed line-clamp-2">{path.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

      {/* Featured Editorial Article — social post style */}
      <section className="py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-3">
            <span className="inline-block px-3 py-1 bg-[#C47F64]/12 text-[#C47F64] text-xs font-medium rounded-full tracking-wider uppercase">
              {language === 'he' ? 'מאמר מערכת' : 'Editorial'}
            </span>
          </div>
          <button
            onClick={openArticle}
            data-testid="featured-article-card"
            className="group block w-full text-right rounded-2xl overflow-hidden bg-white border border-[#EFEAE2] shadow-sm hover:shadow-lg transition-all duration-300"
            dir="rtl"
          >
            {/* Post header */}
            <div className="flex items-center gap-2.5 px-3.5 py-3">
              <div className="p-[2px] rounded-full bg-gradient-to-tr from-[#C47F64] via-[#D9A38C] to-[#4A5D43]">
                <div className="w-9 h-9 rounded-full bg-white p-[2px]">
                  <div className="w-full h-full rounded-full bg-[#2C3329] flex items-center justify-center">
                    <span className="font-serif text-white text-sm leading-none">M</span>
                  </div>
                </div>
              </div>
              <div className="leading-tight">
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-[13px] text-[#1d231b]">M'art Posts</span>
                  <BadgeCheck className="h-3.5 w-3.5 text-[#3897F0]" />
                </div>
                <span className="text-[11px] text-[#9A9790]">{language === 'he' ? 'מקודם · מאמר מערכת' : 'Sponsored · Editorial'}</span>
              </div>
              <MoreHorizontal className="h-5 w-5 text-[#9A9790] mr-auto" />
            </div>

            {/* Media */}
            <div className="relative w-full aspect-square overflow-hidden">
              <img
                src="/images/article-sweat.jpg"
                alt={language === 'he' ? 'המדע מאחורי ריח הזיעה' : 'The science behind body odor'}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                loading="lazy"
              />
            </div>

            {/* Action hints */}
            <div className="px-3.5 pt-3 flex items-center gap-4 text-[#1d231b]">
              <Send className="h-6 w-6" style={{ transform: "scaleX(-1)" }} />
              <MessageCircle className="h-6 w-6" style={{ transform: "scaleX(-1)" }} />
              <Bookmark className="h-6 w-6 mr-auto" />
            </div>

            {/* Caption preview */}
            <div className="px-3.5 pt-2 pb-4">
              <p className="text-[13px] text-[#9A9790] mb-1.5">{language === 'he' ? '10+ משתמשים שיתפו את המאמר' : '10+ people shared this'}</p>
              <p className="text-[14px] leading-relaxed text-[#262219]">
                <span className="font-semibold">mart.posts</span>{' '}
                {language === 'he'
                  ? 'הידעתם? לזיעה שלכם אין ריח. בכלל. 🤯 הריח לא מגיע מהזיעה, אלא מהחיידקים שמפרקים אותה. גלו את המדע המפתיע, ולמה סודה לשתייה היא הפתרון.'
                  : 'Did you know? Your sweat has no smell at all. 🤯 The smell comes from bacteria, not sweat. Discover the surprising science.'}
                <span className="text-[#9A9790] font-medium"> {language === 'he' ? 'קראו עוד' : 'read more'}</span>
              </p>
            </div>
          </button>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-block px-3 py-1 bg-[#4A5D43]/10 text-[#4A5D43] text-xs font-medium rounded-full mb-3 tracking-wider uppercase">
            {language === 'he' ? 'הפילוסופיה שלנו' : 'Our Philosophy'}
          </span>
          <h2 className="font-serif text-xl sm:text-2xl lg:text-3xl text-[#2C3329] mb-3">
            {language === 'he' ? 'למה אנחנו נותנים את המתכון בחינם?' : 'Why do we give the recipe for free?'}
          </h2>
          <p className="text-sm sm:text-base text-[#6B7266] mb-5 leading-relaxed">
            {language === 'he' 
              ? 'כי ידע צריך להיות נגיש. כשיודעים מה בפנים, אפשר לבחור, להתאים, לשפר.'
              : 'Because knowledge should be accessible. When you know what\'s inside, you can choose, adapt, improve.'
            }
          </p>
          <Link to="/why-open">
            <Button 
              variant="outline" 
              data-testid="read-more-philosophy"
              className="border-[#4A5D43] text-[#4A5D43] hover:bg-[#4A5D43] hover:text-white rounded-full px-6 py-4 text-sm"
            >
              {language === 'he' ? 'קראו עוד' : 'Read more'}
              <Arrow className={`${language === 'he' ? 'mr-2' : 'ml-2'} h-4 w-4`} />
            </Button>
          </Link>
        </div>
      </section>

      {/* DIY Kit Teaser - Above Product */}
      <section className="py-6 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/diy-kit">
            <div className="relative premium-card rounded-2xl overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-500 border-2 border-amber-200">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-white to-orange-50" />
              
              <div className="relative flex flex-col md:flex-row items-center gap-4 p-5">
                {/* Image */}
                <div className="w-full md:w-48 h-36 md:h-32 rounded-xl overflow-hidden shrink-0">
                  <img 
                    src={PATH_IMAGES.diyKit}
                    alt={language === 'he' ? 'ערכה להכנה עצמית' : 'DIY Kit'}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1 text-center md:text-right">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                    <Gift className="h-5 w-5 text-amber-600" />
                    <span className="text-[10px] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">
                      {language === 'he' ? 'חדש!' : 'New!'}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl text-[#2C3329] mb-1">
                    {language === 'he' ? 'ערכה להכנה עצמית' : 'DIY Kit'}
                  </h3>
                  <p className="text-sm text-[#6B7266] mb-3">
                    {language === 'he' 
                      ? 'כל החומרים, הכלים וההוראות להכנת 100 גרם דאודורנט טבעי בבית. חוויה יצירתית ומהנה!'
                      : 'All materials, tools, and instructions to make 100g of natural deodorant at home. A creative and enjoyable experience!'
                    }
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-3">
                    <span className="font-serif text-2xl text-amber-600 font-bold">90 ₪</span>
                    <span className="text-sm text-[#6B7266] flex items-center gap-1 group-hover:text-amber-600 transition-colors">
                      {language === 'he' ? 'לפרטים נוספים' : 'Learn more'}
                      <Arrow className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-4 px-4 sm:px-6 section-warm">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <h3 className="font-serif text-lg text-[#2C3329]">
              {language === 'he' ? 'המוצר' : 'The Product'}
            </h3>
            <p className="text-sm text-[#6B7266]">
              {language === 'he' 
                ? '10/50 מ"ל דאודורנט טבעי M\'ART עם כפית במבוק/נירוסטה מצורפת בכל הזמנה'
                : '10/50 ml M\'art natural deodorant with bamboo/stainless steel spoon included with every order'}
            </p>
          </div>
          <ProductGallery language={language} />
        </div>
      </section>

      {/* Coming Soon: Natural Body Soap */}
      <SoapComingSoon />

      {/* Featured Editorial Article Modal */}
      <FeaturedArticleModal isOpen={showArticle} onClose={() => setShowArticle(false)} language={language} />
    </div>
  );
};

// Product Gallery Component with long-press zoom
const ProductGallery = ({ language }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [pressTimer, setPressTimer] = useState(null);
  
  const images = [
    { 
      src: "/images/gallery-4.jpg", 
      alt: language === 'he' ? 'דאודורנט M\'ART 50 מ"ל' : "M'ART Deodorant 50ml",
      description: language === 'he' ? 'דאודורנט טבעי 50 מ"ל - אריזה מלאה' : 'Natural deodorant 50ml - Full size'
    },
    { 
      src: "/images/gallery-2.jpg", 
      alt: language === 'he' ? 'דאודורנט M\'ART בשימוש' : "M'ART Deodorant in use",
      description: language === 'he' ? 'מרקם קרמי ונעים למריחה' : 'Creamy and pleasant texture'
    },
    { 
      src: "/images/gallery-3.jpg", 
      alt: language === 'he' ? 'דאודורנט M\'ART 10 מ"ל' : "M'ART Deodorant 10ml",
      description: language === 'he' ? 'גודל נסיעות 10 מ"ל - לניסיון' : 'Travel size 10ml - For trial'
    },
    { 
      src: "/images/gallery-1.jpg", 
      alt: language === 'he' ? 'מגוון מוצרי M\'ART' : "M'ART product range",
      description: language === 'he' ? 'דאודורנט M\'ART, אריזה וצנצנות במגוון גדלים' : "M'ART deodorant, packaging and jars in various sizes"
    }
  ];

  const handlePressStart = (img) => {
    const timer = setTimeout(() => {
      setSelectedImage(img);
    }, 400);
    setPressTimer(timer);
  };

  const handlePressEnd = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  const handleClick = (img) => {
    setSelectedImage(img);
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {images.map((img, index) => (
          <div 
            key={index} 
            className="image-premium aspect-square relative group cursor-pointer"
            onClick={() => handleClick(img)}
            onMouseDown={() => handlePressStart(img)}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onTouchStart={() => handlePressStart(img)}
            onTouchEnd={handlePressEnd}
          >
            <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end justify-center">
              <span className="text-white text-xs pb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-2 py-1 rounded-full mb-2">
                {language === 'he' ? 'לחץ להגדלה' : 'Click to enlarge'}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] animate-fade-in">
            <img 
              src={selectedImage.src} 
              alt={selectedImage.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
              <p className="text-white text-center font-medium">{selectedImage.alt}</p>
              <p className="text-white/70 text-center text-sm">{selectedImage.description}</p>
            </div>
            <button 
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
